// @flow
import _ from 'lodash'
import type { State } from './reducers'
import type {
  FbPlacements,
  Segment,
  Contact,
  Lookalike,
  ProviderTargetingSource,
  GroupedObjectParameters,
  AudienceNameParts
} from '../../../components/types'
import { Audience } from '../../../components/constants'

const ruT = {
  name_parts: {
    male_abbr: 'М',
    female_abbr: 'Ж',
    to: 'до',
    desktop_abbr: 'Web',
    mobile_abbr: 'Mob',
    keywords_abbr: 'Ключи',
    groups_abbr: 'Сообщ',
    segment_abbr: 'Сегменты',
    contact_abbr: 'Контакты',
    lookalike_segment_abbr: 'LAL сегменты',
    lookalike_contact_abbr: 'LAL контакты',
    app_abbr: 'Прил',
    activity_abbr: 'Актив',
    interest_abbr: 'Интересы',
    provider_targeting_source_abbr: 'Импорт. сегменты',
    format: {
      promo_post: 'Пост',
      multi_format: 'МФ',
      column: 'ТГБ',
      with_video: 'Видео',
      carousel: 'Карусель',
      posts_promotion: 'Натив',
      product_retargeting: 'ДинРем'
    }
  },
  provider_names: {
    facebook: 'FB',
    google: 'Google',
    target_mail: 'MT',
    vk: 'VK'
  }
}

const enT = {
  name_parts: {
    male_abbr: 'M',
    female_abbr: 'F',
    to: 'to',
    desktop_abbr: 'Web',
    mobile_abbr: 'Mob',
    keywords_abbr: 'Keys',
    groups_abbr: 'Groups',
    segment_abbr: 'Segments',
    contact_abbr: 'Contacts',
    lookalike_segment_abbr: 'LAL segments',
    lookalike_contact_abbr: 'LAL contacts',
    app_abbr: 'Apps',
    activity_abbr: 'Activity',
    interest_abbr: 'Interests',
    provider_targeting_source_abbr: 'Provider segments',
    format: {
      promo_post: 'Post',
      multi_format: 'MF',
      column: 'Column',
      with_video: 'Video',
      carousel: 'Carousel',
      posts_promotion: 'Native',
      product_retargeting: 'DynRem'
    }
  },
  provider_names: {
    facebook: 'FB',
    google: 'Google',
    target_mail: 'TM',
    vk: 'VK'
  }
}

export default function getAudienceNameParts(
  state: State,
  segmentation: Object,
  language: string
): AudienceNameParts {
  const t = language === 'RU' ? ruT : enT
  return {
    prefix: prefixPart(state),
    provider: provider(t, state),
    format: format(t, state),
    platform: platform(t, state),
    keywords: keyword(t, state),
    includedGroups: includedGroups(t, state),
    includedVkApps: includedVkApps(t, state),
    vkActivity: vkActivity(t, state),
    segments: targetingSource(state.segments, state.includedSegmentIds, t.name_parts.segment_abbr),
    contacts: targetingSource(state.contacts, state.includedContactIds, t.name_parts.contact_abbr),
    lookalikeSegments: targetingSource(
      state.lookalikeSegments,
      state.includedLookalikeSegmentIds,
      t.name_parts.lookalike_segment_abbr
    ),
    lookalikeContacts: targetingSource(
      state.lookalikeContacts,
      state.includedLookalikeContactIds,
      t.name_parts.lookalike_contact_abbr
    ),
    providerTargetingSources: targetingSource(
      state.providerTargetingSources,
      state.includedProviderTargetingSourceIds,
      t.name_parts.provider_targeting_source_abbr
    ),
    interests: interests(t, state),
    gender: gender(t, state, segmentation),
    age: age(t, state, segmentation),
    location: location(state, segmentation)
  }
}

function prefixPart(state: State) {
  return state.prefixName.replace(/^\s+/, '').replace(/\s+$/, '')
}

function age(t: Object, state: State, segmentation): string {
  if (!segmentation.segmentByAge) return ''

  let ages = ''
  if (state.minAge) ages += state.minAge
  if (state.maxAge) {
    ages += state.minAge ? '-' : t.name_parts.to + ' '
    ages += state.maxAge
  } else {
    if (state.minAge) ages += '+'
  }
  return ages
}

function gender(t: Object, state: State, segmentation) {
  if (!segmentation.segmentByGender) return ''

  if (state.sex === 'male') return t.name_parts.male_abbr
  if (state.sex === 'female') return t.name_parts.female_abbr
  return ''
}

function location(state: State, segmentation): string {
  if (!segmentation.segmentByLocations) return ''

  if (state.includedLocationGroups.length === 0) {
    return localGeo(state)
  } else {
    return includedLocations(state)
  }
}

function localGeo(state: State): string {
  if (state.localGeo.length !== 1) return ''
  const geo = _.first(state.localGeo)
  return _.isEmpty(geo.name)
    ? [_.round(geo.latitude, 4), _.round(geo.longitude, 4)].join(', ')
    : geo.name
}

function includedLocations(state: State): string {
  const includedGroup = _.first(state.includedLocationGroups)
  if (state.useLocationGroupNames && !_.isEmpty(includedGroup.name)) {
    return includedGroup.name
  } else {
    let selectedLocations = _.compact(
      _.map(includedGroup.value, (id) => _.find(state.preloadedLocations, { id: id }))
    )
    if (selectedLocations.length !== 0 && selectedLocations.length <= 2) {
      const locationsString = _.map(selectedLocations, (item) => item.label).join(', ')
      if (selectedLocations.length === 1 || locationsString.length <= 20) {
        return locationsString
      }
    }
    return ''
  }
}

type FbPlatformPosition = {| platform: string, position: string |}

function provider(t: Object, state: State): string {
  if (_.isEqual(state.providers, ['vk'])) return t.provider_names.vk
  if (_.isEqual(state.providers, ['target_mail'])) return t.provider_names.target_mail
  if (_.isEqual(state.providers, ['google'])) return t.provider_names.google
  if (_.isEqual(state.providers, ['facebook'])) {
    const stateFbPlacements = state.fbPlacements
    const placements = fbPlacementsAsArray(stateFbPlacements)
    const fbName = t.provider_names.facebook
    if (placements.length === 1) {
      const placement = placements[0]
      if (placement.platform === 'facebook') {
        if (placement.position === 'feed') return `${fbName}_feed`
        if (placement.position === 'right_hand_column') return `${fbName}_col`
      }
      if (placement.platform === 'instagram') {
        if (placement.position === 'stream') return 'Insta'
        if (placement.position === 'story') return 'Story'
      }
      if (placement.platform === 'messenger' && placement.position === 'messenger_home') {
        return `${fbName}_msg`
      }
    }
    if (placements.length === 2) {
      const facebook = stateFbPlacements.facebook
      const instagram = stateFbPlacements.instagram
      const audience = stateFbPlacements.audience_network

      if (facebook && _.isEqual(_.sortBy(facebook), ['feed', 'instant_article']))
        return `${fbName}_feed`
      if (facebook && audience && _.isEqual(facebook, ['feed'])) return `${fbName}_an`
      if (facebook && instagram && _.isEqual(facebook, ['story'])) return 'Story'
    }

    return fbName
  }

  return ''
}

function fbPlacementsAsArray(fbPlacements: FbPlacements): Array<FbPlatformPosition> {
  const result: Array<FbPlatformPosition> = []

  _.map(fbPlacements, (positions, platform: string) =>
    _.map(positions, (position) => {
      result.push({ platform: platform, position: position })
    })
  )
  return result
}

function platform(t: Object, state: State) {
  if (state.platform === 'desktop') return t.name_parts.desktop_abbr
  if (state.platform === 'mobile') return t.name_parts.mobile_abbr
  return ''
}

function keyword(t: Object, state: State) {
  const firstKeywordGroup = _.first(state.keywordsGroups)
  if (_.isEmpty(firstKeywordGroup.value)) return ''
  if (state.useKeywordsGroupNames) {
    return `${t.name_parts.keywords_abbr}_${firstKeywordGroup.name}`
  } else {
    if (firstKeywordGroup.value.split('\n').length > 1) return t.name_parts.keywords_abbr
    return firstKeywordGroup.value
  }
}

function includedVkApps(t: Object, state: State) {
  const included = _.first(state.includedVkAppsGroups)
  if (_.isEmpty(included.value)) return ''
  if (state.useVkAppsGroupNames) {
    return `${t.name_parts.app_abbr}_${included.name}`
  } else {
    if (included.value.split('\n').length > 1) return t.name_parts.app_abbr
    return `${t.name_parts.app_abbr}_${included.value}`
  }
}

function includedGroups(t: Object, state: State) {
  return buildGroupsName(
    _.first(state.includedGroups),
    t.name_parts.groups_abbr,
    state.useVkGroupNames
  )
}

function vkActivity(t: Object, state: State) {
  return buildGroupsName(
    _.first(state.vkActivityGroups),
    t.name_parts.activity_abbr,
    state.useVkActivityGroupNames
  )
}

function buildGroupsName(
  group: GroupedObjectParameters,
  abbr: string,
  useGroupName: boolean
): string {
  if (_.isEmpty(group.value)) return ''
  if (group.value.length > 1) return abbr
  let name = useGroupName ? group.name : _.first(group.value).short_name
  return `${abbr}_${name}`
}

function targetingSource(
  sources: Array<Segment> | Array<Contact> | Array<Lookalike> | Array<ProviderTargetingSource>,
  sourceIds: Array<number>,
  abbr: string
) {
  if (_.isEmpty(sourceIds)) return ''
  if (sourceIds.length > 1) return abbr
  const source = _.find(sources, (source) => {
    return source.id === _.first(sourceIds)
  })
  return (source && source.name) || ''
}

function interests(t: Object, state: State) {
  if (_.isEmpty(state.providerInterests)) return ''
  if (_.isEmpty(state.interestIds) && _.isEmpty(state.fbAdInterests)) return ''
  if (!_.isEmpty(state.interestIds)) {
    if (state.interestIds.length > 1) return t.name_parts.interest_abbr

    const interest = _.find(state.providerInterests, (interest) => {
      return _.isEqual(interest.id, state.interestIds[0])
    })

    return interest ? interest.label : ''
  } else {
    if (state.fbAdInterests.length > 1) return t.name_parts.interest_abbr

    return (_.first(state.fbAdInterests) && _.first(state.fbAdInterests).label) || ''
  }
}

function format(t: Object, state: State) {
  if (
    _.includes(
      [Audience.types.product_retargeting, Audience.types.posts_promotion],
      state.audienceType
    )
  ) {
    return t.name_parts.format[state.audienceType]
  }
  return t.name_parts.format[state.placementTypes[0]]
}
